import { createFileRoute } from "@tanstack/react-router";
import React, { useState } from "react";
import { TableSkeleton } from "@/components/shared/Skeletons";
import {
  Award,
  Briefcase,
  Star,
  Target,
  Eye,
  Plus,
  Pencil,
  Trash2,
  BadgeCheck,
} from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { IconAction } from "@/components/shared/IconAction";
import { SearchInput } from "@/components/shared/SearchInput";
import { Field } from "@/components/forms";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  useAdminCertificates,
  useCreateCertificateTemplate,
  useUpdateCertificateTemplate,
  useDeleteCertificateTemplate,
} from "@/hooks/useAdminData";

export const Route = createFileRoute("/admin/certificates/")({ component: AdminCertificates });

const badgeIcons: Record<string, React.ReactNode> = {
  award: <Award />,
  briefcase: <Briefcase />,
  star: <Star />,
  target: <Target />,
};

const typeLabels: Record<string, string> = {
      completion: "Completion",
      participation: "Participation",
      excellence: "Excellence",
      achievement: "Achievement",
      placement: "Placement",
    };

const badgeOptions = [
  { value: "award", label: "Award Trophy" },
  { value: "briefcase", label: "Briefcase" },
  { value: "star", label: "Star" },
  { value: "target", label: "Target" },
];

/**
 * Miniature of the issued certificate. The template's own border colour is user data, so it
 * is passed through a CSS variable and only used for the seal, the type label and the frame.
 */
function RealCertificatePreview({ template }: { template: any }) {
  const borderColor = template.border_color || "#1A56DB";
  const label = typeLabels[template.type] || "Completion";
  const badgeEl = badgeIcons[template.badge_icon] || <Award />;

  return (
    <div
      className="rounded-md border border-border bg-card p-2.5"
      style={{ "--cert": borderColor } as React.CSSProperties}
    >
      <div className="rounded-sm border border-[color-mix(in_oklab,var(--cert)_35%,var(--border))] p-1.5">
        <div className="relative overflow-hidden rounded-xs border border-border bg-background px-4 py-5 text-center">
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center text-[var(--cert)] opacity-[0.06] [&_svg]:size-28"
            aria-hidden
          >
            {badgeEl}
          </div>

          <div className="relative">
            <span
              className="mx-auto mb-2 flex size-9 items-center justify-center rounded-full border border-[color-mix(in_oklab,var(--cert)_45%,transparent)] text-[var(--cert)] [&_svg]:size-4"
              aria-hidden
            >
              {badgeEl}
            </span>
            <p className="font-mono text-[9px] tracking-[0.3em] text-muted-foreground uppercase">
              Certificate of
            </p>
            <p className="font-serif text-lg leading-6 text-[var(--cert)]">{label}</p>
            <div className="mx-auto my-2 h-px w-10 bg-border" />
            <p className="font-serif text-[15px] leading-5 text-foreground">
              {template.name || "Certificate Title"}
            </p>
            {template.description && (
              <p className="mx-auto mt-1 line-clamp-2 max-w-60 text-[10px] leading-4 text-muted-foreground italic">
                {template.description}
              </p>
            )}
          </div>

          <div className="relative mt-4 flex items-end justify-between border-t border-border px-1 pt-2">
            <SignatureLine label="Authorised" />
            <span className="font-mono text-[8px] text-muted-foreground">TP-CERT-{template.id}</span>
            <SignatureLine label="Director" />
          </div>
        </div>
      </div>
    </div>
  );
}

function SignatureLine({ label }: { label: string }) {
  return (
    <div className="text-center">
      <div className="mx-auto mb-0.5 h-px w-9 bg-hairline-strong" />
      <p className="font-mono text-[7px] tracking-[0.12em] text-muted-foreground uppercase">{label}</p>
    </div>
  );
}

function ColorInput({
  value,
  onChange,
  placeholder,
  ...aria
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  id?: string;
  "aria-describedby"?: string;
}) {
  return (
    <div className="relative">
      <span
        className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 rounded-xs border border-border"
        style={{ background: value }}
        aria-hidden
      />
      <Input
        {...aria}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pl-9 font-mono"
      />
    </div>
  );
}

interface TemplateFieldsProps {
  name: string;
  setName: (v: string) => void;
  type: string;
  setType: (v: string) => void;
  badge: string;
  setBadge: (v: string) => void;
  borderColor: string;
  setBorderColor: (v: string) => void;
  accentColor: string;
  setAccentColor: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  bodyText: string;
  setBodyText: (v: string) => void;
  signatoryName: string;
  setSignatoryName: (v: string) => void;
  signatoryTitle: string;
  setSignatoryTitle: (v: string) => void;
  withPlaceholders?: boolean;
}

function TemplateFields(p: TemplateFieldsProps) {
  const ph = (text: string) => (p.withPlaceholders ? text : undefined);
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Template name" className="sm:col-span-2">
        <Input
          value={p.name}
          onChange={(e) => p.setName(e.target.value)}
          placeholder={ph("e.g. Field Attachment Completion")}
        />
      </Field>
      <Field label="Certificate type">
        {({ id }) => (
          <Select value={p.type} onValueChange={p.setType}>
            <SelectTrigger id={id}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="completion">Completion</SelectItem>
              <SelectItem value="participation">Participation</SelectItem>
              <SelectItem value="excellence">Excellence</SelectItem>
              <SelectItem value="achievement">Achievement</SelectItem>
              <SelectItem value="placement">Placement</SelectItem>
            </SelectContent>
          </Select>
        )}
      </Field>
      <Field label="Badge icon">
        {({ id }) => (
          <Select value={p.badge} onValueChange={p.setBadge}>
            <SelectTrigger id={id}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {badgeOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>
      <Field label="Border color">
        {({ id, describedBy }) => (
          <ColorInput
            id={id}
            aria-describedby={describedBy}
            value={p.borderColor}
            onChange={p.setBorderColor}
            placeholder={ph("#1A56DB")}
          />
        )}
      </Field>
      <Field label="Accent color">
        {({ id, describedBy }) => (
          <ColorInput
            id={id}
            aria-describedby={describedBy}
            value={p.accentColor}
            onChange={p.setAccentColor}
            placeholder={ph("#3B82F6")}
          />
        )}
      </Field>
      <Field label="Description" className="sm:col-span-2">
        <Textarea
          rows={2}
          value={p.description}
          onChange={(e) => p.setDescription(e.target.value)}
          placeholder={ph("Brief description of this certificate type")}
        />
      </Field>
      <Field
        label="Certificate body text"
        className="sm:col-span-2"
        hint="What the certificate says. Leave blank to use the default wording for the selected type."
      >
        <Textarea
          rows={3}
          value={p.bodyText}
          onChange={(e) => p.setBodyText(e.target.value)}
          placeholder={ph("For having successfully completed the requirements of this programme")}
        />
      </Field>
      <Field label="Signatory name">
        <Input
          value={p.signatoryName}
          onChange={(e) => p.setSignatoryName(e.target.value)}
          placeholder={ph("Dr. A. Mensah")}
        />
      </Field>
      <Field label="Signatory title">
        <Input
          value={p.signatoryTitle}
          onChange={(e) => p.setSignatoryTitle(e.target.value)}
          placeholder={ph("Programme Director")}
        />
      </Field>
    </div>
  );
}

function AdminCertificates() {
  const [search, setSearch] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState("completion");
  const [formBadge, setFormBadge] = useState("award");
  const [formBorderColor, setFormBorderColor] = useState("#1A56DB");
  const [formAccentColor, setFormAccentColor] = useState("#3B82F6");
  const [formDescription, setFormDescription] = useState("");
  const [formBodyText, setFormBodyText] = useState("");
  const [formSignatoryName, setFormSignatoryName] = useState("");
  const [formSignatoryTitle, setFormSignatoryTitle] = useState("");

  const [editName, setEditName] = useState("");
  const [editType, setEditType] = useState("completion");
  const [editBadge, setEditBadge] = useState("award");
  const [editBorderColor, setEditBorderColor] = useState("#1A56DB");
  const [editAccentColor, setEditAccentColor] = useState("#3B82F6");
  const [editDescription, setEditDescription] = useState("");
  const [editBodyText, setEditBodyText] = useState("");
  const [editSignatoryName, setEditSignatoryName] = useState("");
  const [editSignatoryTitle, setEditSignatoryTitle] = useState("");

  const { data: templates = [], isLoading } = useAdminCertificates();
  const createTemplate = useCreateCertificateTemplate();
  const updateTemplate = useUpdateCertificateTemplate();
  const deleteTemplate = useDeleteCertificateTemplate();

  const filteredTemplates = Array.isArray(templates)
    ? templates.filter((t: any) => t.name?.toLowerCase().includes(search.toLowerCase()))
    : [];

  const openEditDialog = (t: any) => {
    setSelectedTemplate(t);
    setEditName(t.name || "");
    setEditType(t.type || "completion");
    setEditBadge(t.badge_icon || "award");
    setEditBorderColor(t.border_color || "#1A56DB");
    setEditAccentColor(t.accent_color || "#3B82F6");
    setEditDescription(t.description || "");
    setEditBodyText(t.body_text || "");
    setEditSignatoryName(t.signatory_name || "");
    setEditSignatoryTitle(t.signatory_title || "");
    setEditDialogOpen(true);
  };

  const handleCreate = () => {
    createTemplate.mutate(
      {
        name: formName,
        type: formType,
        badge_icon: formBadge,
        border_color: formBorderColor,
        accent_color: formAccentColor,
        description: formDescription,
        body_text: formBodyText,
        signatory_name: formSignatoryName,
        signatory_title: formSignatoryTitle,
      },
      {
        onSettled: () => {
          setCreateDialogOpen(false);
          setFormName("");
          setFormType("completion");
          setFormBadge("award");
          setFormBorderColor("#1A56DB");
          setFormAccentColor("#3B82F6");
          setFormDescription("");
          setFormBodyText("");
          setFormSignatoryName("");
          setFormSignatoryTitle("");
        },
      },
    );
  };

  const handleEdit = () => {
    updateTemplate.mutate(
      {
        id: selectedTemplate.id,
        payload: {
          name: editName,
          type: editType,
          badge_icon: editBadge,
          border_color: editBorderColor,
          accent_color: editAccentColor,
          description: editDescription,
          body_text: editBodyText,
          signatory_name: editSignatoryName,
          signatory_title: editSignatoryTitle,
        },
      },
      { onSettled: () => setEditDialogOpen(false) },
    );
  };

  const handleDelete = () => {
    deleteTemplate.mutate(selectedTemplate.id, { onSettled: () => setDeleteDialogOpen(false) });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Certification engine"
        subtitle="Design, manage, and automate the issuance of digital certificates and industrial training credentials."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Certificates" }]}
        action={
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus />
            Design template
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
        <StatCard label="Design templates" value={templates.length} icon={<Award />} />
        <StatCard
          label="Active issuance"
          value={templates.filter((t: any) => t.status === "active").length}
          icon={<BadgeCheck />}
        />
        <StatCard
          label="Total digital issues"
          value={templates.reduce((sum: number, t: any) => sum + (t.issued || 0), 0)}
          icon={<Eye />}
        />
      </div>

      <SectionCard
        title="Template library"
        actions={
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search templates…"
            aria-label="Search templates"
          />
        }
      >
        {isLoading ? (
          <TableSkeleton rows={5} columns={4} />
        ) : filteredTemplates.length === 0 ? (
          <EmptyState
            bare
            icon={<Award />}
            title="No certificate templates found."
            description="Design a template to start issuing certificates."
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {filteredTemplates.map((template: any) => (
              <div key={template.id} className="flex flex-col gap-2">
                <RealCertificatePreview template={template} />
                <div className="flex items-center justify-between gap-2 px-0.5">
                  <Badge variant="muted" className="tabular-nums">
                    {`${template.issued || 0} issued`}
                  </Badge>
                  <div className="flex gap-0.5">
                    <IconAction
                      label="Edit template"
                      icon={<Pencil />}
                      onClick={() => openEditDialog(template)}
                    />
                    <IconAction
                      label="Delete template"
                      tone="destructive"
                      icon={<Trash2 />}
                      onClick={() => {
                        setSelectedTemplate(template);
                        setDeleteDialogOpen(true);
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <Dialog open={createDialogOpen} onOpenChange={(o) => !o && setCreateDialogOpen(false)}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New certificate template</DialogTitle>
            <DialogDescription>Set the wording, signatory and look of the certificate.</DialogDescription>
          </DialogHeader>
          <TemplateFields
            withPlaceholders
            name={formName}
            setName={setFormName}
            type={formType}
            setType={setFormType}
            badge={formBadge}
            setBadge={setFormBadge}
            borderColor={formBorderColor}
            setBorderColor={setFormBorderColor}
            accentColor={formAccentColor}
            setAccentColor={setFormAccentColor}
            description={formDescription}
            setDescription={setFormDescription}
            bodyText={formBodyText}
            setBodyText={setFormBodyText}
            signatoryName={formSignatoryName}
            setSignatoryName={setFormSignatoryName}
            signatoryTitle={formSignatoryTitle}
            setSignatoryTitle={setFormSignatoryTitle}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate}>Create template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={(o) => !o && setEditDialogOpen(false)}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit template</DialogTitle>
            <DialogDescription>Changes apply to certificates issued from now on.</DialogDescription>
          </DialogHeader>
          <TemplateFields
            name={editName}
            setName={setEditName}
            type={editType}
            setType={setEditType}
            badge={editBadge}
            setBadge={setEditBadge}
            borderColor={editBorderColor}
            setBorderColor={setEditBorderColor}
            accentColor={editAccentColor}
            setAccentColor={setEditAccentColor}
            description={editDescription}
            setDescription={setEditDescription}
            bodyText={editBodyText}
            setBodyText={setEditBodyText}
            signatoryName={editSignatoryName}
            setSignatoryName={setEditSignatoryName}
            signatoryTitle={editSignatoryTitle}
            setSignatoryTitle={setEditSignatoryTitle}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEdit}>Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={(o) => !o && setDeleteDialogOpen(false)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete{" "}
              <strong className="font-medium text-foreground">{selectedTemplate?.name}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 />
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
